import {
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  Link,
  Chip,
  Radio,
  Box,
  createFilterOptions,
  Checkbox,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  CustomTabPanel,
  CustomTable,
  CustomeDialog,
  DeleteModal,
  Loader,
  PageListLayout,
  InputControl,
  LimitedTag,
  ServerPaginatedtable,
} from "../../../shared";
import { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useStores } from "../hooks";
import { createColumnHelper } from "@tanstack/react-table";
import { SubRowCustomTable } from "../components";
import { useFinishedProductsList } from "../hooks";
import CachedIcon from "@mui/icons-material/Cached";
import moment from "moment";
import { CustomDialogTemplate } from "../../../shared/components/CustomDialogTemplate";

export const FinishedProductsList = () => {
  let { id } = useParams();
  let navigate = useNavigate();

  const {
    moduleName,
    listQuery,
    tableData,
    state,
    categoryValuesHandler,
    CategoryOptions,
    onClearFilter,
    disableclearButton,
    selectStatus,
    onChangeStatusHandler,
    SubCategoryOptions,
    subCategoryValuesHandler,
    setPagination,
    setSorting,
    pagination,
    sorting,
    onSearchHandler,
    search,
    handleUpdatePOSSync,
    handleChangeDefaultStatus,
    updatePOSSync,
    handleSetDefaultStatus,
  } = useFinishedProductsList(id);

  const { dataList, isLoading } = state;

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.name,
  });

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("plu", {
      header: () => "PLU",
      cell: ({ row, table }) => {
        const currentPlu = row.original.plu;
        const prevRow = table.getRowModel().rows[row.index - 1];

        // Show PLU only if first row OR different from previous row
        if (!prevRow || prevRow.original.plu !== currentPlu) {
          return currentPlu;
        }

        return null; // hide duplicate
      },
    }),

    columnHelper.accessor("name", {
      header: () => "Finished Product Name",
      cell: ({ getValue, row: { original } }) => (
        <Typography
          onClick={() =>
            navigate(`/recipe/details/${original?.id}?basic-details`)
          }
          variant="subtitle2"
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
            color: "neutral.10",
          }}
        >
          {getValue()}
        </Typography>
      ),
    }),

    columnHelper.accessor("category", {
      header: () => "Category",
    }),

    columnHelper.accessor("subcategory", {
      header: () => "Sub-Category",
    }),

    columnHelper.accessor("status", {
      header: () => "Status",
      cell: ({ getValue }) => (
        <Chip
          size="small"
          color={getValue() === 1 ? "primary" : "gray"}
          variant="filled"
          label={getValue() === 1 ? "ACTIVE" : "INACTIVE"}
        />
      ),
    }),

    columnHelper.accessor("cost_exc_gst", {
      header: () => "Cost (Ex GST)",
      cell: ({ getValue }) => `$${getValue().toFixed(3)}`,
    }),
    columnHelper.accessor("set_as_default", {
      header: () => "Set as Default",
      cell: ({ row, getValue }) => {
        return (
          <Radio
            checked={getValue() === 1}
            onChange={() => handleSetDefaultStatus(row.original, true)}
          />
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) => {
        const { row } = props;
        const { last_sync_date, last_sync_status } = row.original;
        return (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="flex-end"
          >
            {last_sync_date && (
              <Typography variant="body2" color="#485364">
                Last Synced: {last_sync_date}{" "}
                <Typography
                  variant="caption"
                  color={last_sync_status === 1 ? "#0B815A" : "#B42318"}
                  sx={{ ml: 0.5 }}
                >
                  {last_sync_status === 1 ? "Success" : "Failed"}
                </Typography>
              </Typography>
            )}
            <Button
              disabled={!row?.original?.set_as_default}
              onClick={() => handleUpdatePOSSync("single", row.original.id)}
            >
              Sync
            </Button>
          </Stack>
        );
      },
    }),
  ];

  return (
    <>
      <ServerPaginatedtable
        defaultData={state.dataList ?? []}
        columns={columns}
        isLoading={listQuery.isLoading}
        pagination={pagination}
        setPagination={(callback) =>
          setPagination({ tableName: moduleName, callback })
        }
        sorting={sorting}
        setSorting={(callback) =>
          setSorting({ tableName: moduleName, callback })
        }
        onSearchChange={onSearchHandler}
        search={search}
        paginationProps={
          listQuery.data?.pagination || { total: 0, lastPage: 1 }
        }
        headerText={
          <Typography variant="h5" fontWeight={500} fontSize="18px">
            Finished Products (With PLU)
          </Typography>
        }
        showSearchBar={true}
        customRowStyle={(row) => {
          if (row.original.firstPlu && row.original.lastPlu) {
            return {
              borderTop: "1px solid #DFE8F2",
              boxShadow: "0 7px 0 #F5F8FC", // gap effect
            };
          }

          if (row.original.firstPlu) {
            return {
              borderTop: "1px solid #DFE8F2",
            };
          }

          if (row.original.lastPlu) {
            return {
              borderBottom: "1px solid #DFE8F2",
              boxShadow: "0 7px 0 #F5F8FC", // creates space below
            };
          }

          return {};
        }}
        emptyTableMessage={"No finished products(PLU) assigned"}
        tableHeaderStyles={{ px: 0, fontSize: "18px", fontWeight: 600 }}
        enableTableContainerPaddingBottom
        tableHeaderContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "6px",
              width: "100%",
            }}
          >
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              fullWidth
              multiple={true}
              options={CategoryOptions ?? []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              sx={{
                width: "250px",
              }}
              renderTags={(value) => (
                <LimitedTag value={value} autocompleteWidth="250px" />
              )}
              value={
                tableData?.finishedProductsList?.data?.categoryfilter ?? []
              }
              onChange={(event, option) => categoryValuesHandler(event, option)}
              renderOption={(props, option, { selected }) => (
                <>
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option?.name}
                  </li>
                </>
              )}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <TextField {...params} placeholder="Category" />
              )}
            />
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              fullWidth
              multiple={true}
              options={SubCategoryOptions ?? []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              sx={{
                width: "250px",
              }}
              renderTags={(value) => (
                <LimitedTag value={value} autocompleteWidth="250px" />
              )}
              value={
                tableData?.finishedProductsList?.data?.subcategoryfilter ?? []
              }
              onChange={(event, option) =>
                subCategoryValuesHandler(event, option)
              }
              renderOption={(props, option, { selected }) => (
                <>
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option?.name}
                  </li>
                </>
              )}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <TextField {...params} placeholder="Sub-Category" />
              )}
            />
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={selectStatus.statusOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{
                width: "180px",
              }}
              value={
                selectStatus?.statusOptions?.find(
                  (option) =>
                    option.id ===
                    tableData?.finishedProductsList?.data?.selectedStatus,
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Status" />
              )}
              onChange={(event, option) =>
                selectStatus.statusValueChangeHandler(option)
              }
            />
            <Button
              type="button"
              size="small"
              variant="text"
              sx={{
                p: "6px 12px 6px 12px",
                gap: "6px",
              }}
              disabled={disableclearButton}
              onClick={onClearFilter}
            >
              Clear All
            </Button>
          </Stack>
        }
        rightContent={
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" color="#485364">
              Last Synced: {listQuery?.data?.last_synced_at}{" "}
              <Typography
                variant="caption"
                color={
                  listQuery?.data?.last_sync_status === 1
                    ? "#0B815A"
                    : "#B42318"
                }
                sx={{ ml: 0.5 }}
              >
                {listQuery?.data?.last_sync_status === 1 ? "Success" : "Failed"}
              </Typography>
            </Typography>
            <Button
              type="button"
              size="medium"
              variant="contained"
              startIcon={!updatePOSSync.isPending && <CachedIcon />}
              onClick={handleUpdatePOSSync}
              disabled={updatePOSSync.isPending}
            >
              {updatePOSSync.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Sync"
              )}
            </Button>
          </Stack>
        }
      />
      <CustomDialogTemplate
        PaperProps={{
          sx: {
            minWidth: { md: "459px", sm: "80%", xs: "100%" },
            height: "fit-content",
          },
        }}
        title={
          <Stack direction="row" alignItems="center" gap="8px">
            <img
              src="/icons/ic_warning.svg"
              alt="Schedule Sync"
              style={{
                padding: "6px",
                backgroundColor: "#F044381F",
                borderRadius: "50%",
              }}
              width={32}
              height={32}
            />
            <Typography variant="h6" fontWeight={600}>
              Set as Default?
            </Typography>
          </Stack>
        }
        closeIcon={false}
        open={state.isModalOpen}
        content={
          <Stack
            sx={{
              p: "16px 24px",
              gap: "12px",
              height: "fit-content",
            }}
          >
            <Typography>
              This will set <b>“{state.isDefault?.name}”</b> as the default
              recipe for <b>{state.isDefault?.plu}</b>. Any existing default
              recipe will be replaced. Do you want to continue?
            </Typography>

            <Stack
              direction="row"
              justifyContent="flex-end"
              padding="10px"
              marginTop="auto"
              gap="8px"
              onClick={() => handleSetDefaultStatus(null, false)}
            >
              <Button key="cancel" variant="outlined" color="gray2">
                No
              </Button>

              <Button
                key="generate"
                variant="contained"
                onClick={() => handleChangeDefaultStatus(state.isDefault?.id)}
              >
                {false ? <CircularProgress size={20} color="inherit" /> : "Yes"}
              </Button>
            </Stack>
          </Stack>
        }
      />
    </>
  );
};
